import React, { useState, useEffect } from 'react';
import { 
  Card, Typography, Space, Button, Table, Tag, Modal, Form, Input, Select, 
  DatePicker, Row, Col, Upload, Image, message, Popconfirm, Descriptions,
  Badge, Divider, Alert, Spin, Statistic, InputNumber
} from 'antd';
import { 
  PlusOutlined, EditOutlined, DeleteOutlined, WarningOutlined, HistoryOutlined, 
  FilterOutlined, UploadOutlined, EyeOutlined, FileImageOutlined, SoundOutlined,
  VideoCameraOutlined, CloseOutlined, ExclamationCircleOutlined, CloseCircleOutlined,
  SearchOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';
import studentAPI from '../../services/studentAPI';
import api from '../../services/api';
import moment from 'moment';
import { downloadCsv, fetchAllPages } from '../../utils/csvExport';

const { Title, Paragraph, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { RangePicker } = DatePicker;

const getUserSchoolId = (user) => (
  user?.schoolId || user?.school_id || user?.schoolID || user?.schoolIds?.[0] || user?.school_ids?.[0] || null
);

const StudentViolations = () => {
  const { user } = useAuth();
  const [form] = Form.useForm();
  const [filterForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [violations, setViolations] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState(null);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [filters, setFilters] = useState({});
  const [modalVisible, setModalVisible] = useState(false);
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [previewImage, setPreviewImage] = useState('');
  const [previewVisible, setPreviewVisible] = useState(false);

  // 违纪类型选项
  const violationTypes = [
    { value: 'late', label: '迟到', color: 'orange' },
    { value: 'absence', label: '旷课', color: 'red' },
    { value: 'fighting', label: '打架', color: 'red' },
    { value: 'cheating', label: '作弊', color: 'red' },
    { value: 'disruption', label: '课堂扰乱', color: 'orange' },
    { value: 'homework', label: '作业未完成', color: 'yellow' },
    { value: 'uniform', label: '违反校服规定', color: 'blue' },
    { value: 'phone', label: '违规使用手机', color: 'orange' },
    { value: 'other', label: '其他', color: 'default' },
  ];

  // 严重程度选项
  const severityOptions = [
    { value: 'light', label: '轻微', color: 'green' },
    { value: 'serious', label: '严重', color: 'orange' },
    { value: 'severe', label: '恶劣', color: 'red' },
  ];

  // 处理状态选项
  const statusOptions = [
    { value: 'pending', label: '待处理', color: 'orange' },
    { value: 'processed', label: '已处理', color: 'green' },
    { value: 'appealed', label: '申诉中', color: 'blue' },
  ];

  // 获取违纪记录列表
  const fetchViolations = async (params = {}) => {
    setLoading(true);
    try {
      const response = await studentAPI.violations.getViolations({
        page: pagination.current,
        pageSize: pagination.pageSize,
        ...filters,
        ...params,
      });
      
      if (response.data.success) {
        setViolations(response.data.data);
        setPagination(prev => ({
          ...prev,
          total: response.data.pagination.total,
        }));
      }
    } catch (error) {
      message.error('获取违纪记录失败');
      console.error('获取违纪记录失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 筛选栏班级变化时，动态加载对应的学生
  const handleFilterClassChange = async (classId) => {
    filterForm.setFieldsValue({ studentId: undefined });
    if (classId) {
      try {
        const response = await api.get('/students', {
          params: { classId }
        });
        if (response.data) {
          const studentData = response.data.success ? response.data.data : response.data;
          setStudents(Array.isArray(studentData) ? studentData : []);
        }
      } catch (error) {
        console.error('获取班级学生失败:', error);
        setStudents([]);
      }
    } else {
      setStudents([]);
    }
  };

  // 获取班级列表
  const fetchClasses = async () => {
    try {
      const response = await api.get('/classes');
      if (response.data.success) {
        setClasses(response.data.data);
      }
    } catch (error) {
      console.error('获取班级列表失败:', error);
    }
  };
  
  // 根据班级ID筛选学生（重构为按需从云端精准拉取该班级学生）
  const handleClassChange = async (classId) => {
    setSelectedClassId(classId);
    if (classId) {
      try {
        const response = await api.get('/students', {
          params: { classId }
        });
        if (response.data) {
          const studentData = response.data.success ? response.data.data : response.data;
          setFilteredStudents(Array.isArray(studentData) ? studentData : []);
        }
      } catch (error) {
        console.error('获取班级学生列表失败:', error);
        message.error('获取班级学生列表失败');
        setFilteredStudents([]);
      }
    } else {
      setFilteredStudents([]);
    }
    form.setFieldsValue({ student_id: undefined, student_name: undefined });
  };

  useEffect(() => {
    fetchViolations();
    fetchClasses();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchViolations();
  }, [pagination.current, pagination.pageSize, filters]);

  // 处理表格变化
  const handleTableChange = (newPagination, tableFilters, sorter) => {
    setPagination(newPagination);
  };

  // 处理筛选
  const handleFilter = (values) => {
    const nextFilters = { ...values };
    if (values.violationType) {
      nextFilters.violation_type = values.violationType;
      delete nextFilters.violationType;
    }
    if (Array.isArray(values.dateRange) && values.dateRange.length === 2) {
      nextFilters.startDate = values.dateRange[0]?.format('YYYY-MM-DD');
      nextFilters.endDate = values.dateRange[1]?.format('YYYY-MM-DD');
      delete nextFilters.dateRange;
    }
    setFilters(nextFilters);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 重置筛选
  const resetFilters = () => {
    filterForm.resetFields();
    setFilters({});
    setStudents([]);
    setPagination(prev => ({ ...prev, current: 1 }));
  };

  // 打开新增/编辑模态框
  const openModal = (record = null) => {
    setEditingRecord(record);
    setModalVisible(true);
    setFileList([]);
    
    if (record) {
      form.setFieldsValue({
        ...record,
        violation_date: record.violation_date ? moment(record.violation_date) : null,
      });
      // 如果是编辑，设置班级并从服务器按需拉取该班级学生
      if (record.class_id) {
        setSelectedClassId(record.class_id);
        api.get('/students', { params: { classId: record.class_id } }).then(response => {
          if (response.data) {
            const studentData = response.data.success ? response.data.data : response.data;
            const fetched = Array.isArray(studentData) ? studentData : [];
            const hasStudent = fetched.some(s => s.id === record.student_id);
            if (!hasStudent && record.student_id) {
              fetched.push({
                id: record.student_id,
                student_name: record.student_name,
                class_id: record.class_id
              });
            }
            setFilteredStudents(fetched);
          }
        }).catch(err => {
          console.error('获取编辑 student 班级列表失败:', err);
          if (record.student_id) {
            setFilteredStudents([{
              id: record.student_id,
              student_name: record.student_name,
              class_id: record.class_id
            }]);
          }
        });
      }
    } else {
      form.resetFields();
      setSelectedClassId(null);
      setFilteredStudents([]);
    }
  };

  // 关闭模态框
  const closeModal = () => {
    setModalVisible(false);
    setEditingRecord(null);
    setFileList([]);
    setSelectedClassId(null);
    setFilteredStudents([]);
    form.resetFields();
  };

  // 查看详情
  const viewDetail = async (record) => {
    try {
      const response = await studentAPI.violations.getViolationDetail(record.id);
      if (response.data.success) {
        setSelectedRecord(response.data.data);
        setDetailModalVisible(true);
      }
    } catch (error) {
      message.error('获取详情失败');
    }
  };

  // 提交表单
  const handleSubmit = async (values) => {
    try {
      // 获取选中的学生信息（重构为从 modal 的 filteredStudents 中匹配，避免依赖全局大表）
      const selectedStudent = filteredStudents.find(s => s.id === values.student_id);
      if (!selectedStudent) {
        message.error('请选择学生');
        return;
      }

      const formData = {
        student_id: selectedStudent.id,
        student_name: selectedStudent.student_name,
        class_id: selectedStudent.class_id || null,
        school_id: selectedStudent.school_id
          || classes.find(cls => String(cls.id) === String(selectedStudent.class_id || selectedClassId))?.school_id
          || getUserSchoolId(user)
          || null,
        violation_type: values.violation_type,
        severity: values.severity,
        title: values.title,
        description: values.description,
        location: values.location,
        punishment: values.punishment,
        status: values.status || 'pending',
        points_deducted: values.points_deducted || 0,
        violation_date: values.violation_date ? values.violation_date.format('YYYY-MM-DD HH:mm:ss') : moment().format('YYYY-MM-DD HH:mm:ss'),
        attachments: fileList,
      };

      if (editingRecord) {
        await studentAPI.violations.updateViolation(editingRecord.id, formData);
        message.success('违纪记录更新成功');
      } else {
        await studentAPI.violations.createViolation(formData);
        message.success('违纪记录创建成功');
      }
      
      closeModal();
      fetchViolations();
    } catch (error) {
      message.error(editingRecord ? '更新失败' : '创建失败');
      console.error('提交失败:', error);
    }
  };

  // 删除记录
  const handleDelete = async (id) => {
    try {
      await studentAPI.violations.deleteViolation(id);
      message.success('删除成功');
      fetchViolations();
    } catch (error) {
      message.error('删除失败');
      console.error('删除失败:', error);
    }
  };

  // 文件上传处理
  const handleUploadChange = ({ fileList: newFileList }) => {
    setFileList(newFileList);
  };

  // 预览文件
  const handlePreview = async (file) => {
    if (!file.url && !file.preview) {
      file.preview = await getBase64(file.originFileObj);
    }
    setPreviewImage(file.url || file.preview);
    setPreviewVisible(true);
  };

  // 获取base64
  const getBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  const handleExportRecords = async () => {
    try {
      message.loading({ content: '正在导出违纪记录...', key: 'export-violations', duration: 0 });
      const schoolId = getUserSchoolId(user);
      const rows = await fetchAllPages(studentAPI.violations.getViolations, {
        ...(schoolId ? { schoolId } : {}),
        ...filters
      });
      if (rows.length === 0) {
        message.warning({ content: '没有可导出的违纪记录', key: 'export-violations' });
        return;
      }
      downloadCsv(`违纪记录-${moment().format('YYYYMMDD-HHmmss')}.csv`, [
        { title: '学生姓名', dataIndex: 'student_name' },
        { title: '班级', dataIndex: 'class_name' },
        { title: '违纪类型', render: (row) => violationTypes.find((item) => item.value === row.violation_type)?.label || row.violation_type },
        { title: '严重程度', render: (row) => severityOptions.find((item) => item.value === row.severity)?.label || row.severity },
        { title: '违纪标题', dataIndex: 'title' },
        { title: '状态', render: (row) => statusOptions.find((item) => item.value === row.status)?.label || row.status },
        { title: '扣除积分', dataIndex: 'points_deducted' },
        { title: '违纪时间', render: (row) => row.violation_date ? moment(row.violation_date).format('YYYY-MM-DD HH:mm:ss') : '' },
        { title: '处理措施', dataIndex: 'punishment' }
      ], rows);
      message.success({ content: `已导出 ${rows.length} 条违纪记录`, key: 'export-violations' });
    } catch (error) {
      console.error('导出违纪记录失败:', error);
      message.error({ content: '导出违纪记录失败', key: 'export-violations' });
    }
  };

  // 渲染附件
  const renderAttachments = (attachments) => {
    if (!attachments || attachments.length === 0) {
      return <Text type="secondary">无附件</Text>;
    }

    return (
      <Space wrap>
        {attachments.map((attachment, index) => {
          const isImage = attachment.file_type === 'image';
          const isAudio = attachment.file_type === 'audio';
          const isVideo = attachment.file_type === 'video';

          return (
            <div key={index} style={{ display: 'inline-block', margin: '4px' }}>
              {isImage && (
                <Image
                  width={60}
                  height={60}
                  src={`${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100')}${attachment.file_path}`}
                  style={{ objectFit: 'cover', borderRadius: '4px' }}
                  preview={{
                    src: `${process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://127.0.0.1:4100')}${attachment.file_path}`,
                  }}
                />
              )}
              {isAudio && (
                <div style={{ 
                  width: 60, height: 60, display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', background: '#f0f0f0', borderRadius: '4px' 
                }}>
                  <SoundOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                </div>
              )}
              {isVideo && (
                <div style={{ 
                  width: 60, height: 60, display: 'flex', alignItems: 'center', 
                  justifyContent: 'center', background: '#f0f0f0', borderRadius: '4px' 
                }}>
                  <VideoCameraOutlined style={{ fontSize: '24px', color: '#1890ff' }} />
                </div>
              )}
            </div>
          );
        })}
      </Space>
    );
  };

  // 表格列定义
  const columns = [
    {
      title: '学生姓名',
      dataIndex: 'student_name',
      key: 'student_name',
      width: 100,
      fixed: 'left',
    },
    {
      title: '班级',
      dataIndex: 'class_name',
      key: 'class_name',
      width: 100,
    },
    {
      title: '违纪类型',
      dataIndex: 'violation_type',
      key: 'violation_type',
      width: 120,
      render: (type) => {
        const option = violationTypes.find(item => item.value === type);
        return <Tag color={option?.color}>{option?.label || type}</Tag>;
      },
    },
    {
      title: '严重程度',
      dataIndex: 'severity',
      key: 'severity',
      width: 100,
      render: (severity) => {
        const option = severityOptions.find(item => item.value === severity);
        return <Tag color={option?.color}>{option?.label || severity}</Tag>;
      },
    },
    {
      title: '违纪标题',
      dataIndex: 'title',
      key: 'title',
      ellipsis: true,
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const option = statusOptions.find(item => item.value === status);
        return <Badge color={option?.color} text={option?.label || status} />;
      },
    },
    {
      title: '扣除积分',
      dataIndex: 'points_deducted',
      key: 'points_deducted',
      width: 100,
      render: (points) => points > 0 ? <Text type="danger">-{points}</Text> : '-',
    },
    {
      title: '附件',
      dataIndex: 'attachment_count',
      key: 'attachment_count',
      width: 80,
      render: (count) => count > 0 ? <Badge count={count} size="small" /> : '-',
    },
    {
      title: '违纪时间',
      dataIndex: 'violation_date',
      key: 'violation_date',
      width: 120,
      render: (date) => moment(date).format('YYYY-MM-DD'),
    },
    {
      title: '操作',
      key: 'actions',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => viewDetail(record)}
          >
            详情
          </Button>
          <Button
            type="link"
            size="small"
            icon={<EditOutlined />}
            onClick={() => openModal(record)}
          >
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这条违纪记录吗？"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button
              type="link"
              size="small"
              danger
              icon={<DeleteOutlined />}
            >
              删除
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      {/* 违纪统计 */}
      <Row gutter={16} style={{ marginBottom: 16 }}>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="本月违纪总数"
              value={violations.length}
              prefix={<ExclamationCircleOutlined style={{ color: '#f5222d' }} />}
              valueStyle={{ color: '#f5222d' }}
              suffix="次"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="待处理记录"
              value={violations.filter(v => v.status === 'pending').length}
              prefix={<WarningOutlined style={{ color: '#fa8c16' }} />}
              valueStyle={{ color: '#fa8c16' }}
              suffix="条"
            />
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card size="small">
            <Statistic
              title="严重违纪"
              value={violations.filter(v => v.severity === 'severe').length}
              prefix={<CloseCircleOutlined style={{ color: '#eb2f96' }} />}
              valueStyle={{ color: '#eb2f96' }}
              suffix="次"
            />
          </Card>
        </Col>
      </Row>

      {/* 筛选区域 */}
      <Card style={{ marginBottom: 16 }}>
        <Form
          form={filterForm}
          layout="inline"
          onFinish={handleFilter}
        >
          <Form.Item name="studentId" label="学生">
            <Select
              placeholder="选择学生"
              style={{ width: 200 }}
              allowClear
              showSearch
              optionFilterProp="children"
              filterOption={(input, option) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
            >
              {students.map(student => (
                <Option key={student.id} value={student.id}>
                  {student.student_name} - {student.class_name || '未分班'}
                </Option>
              ))}
            </Select>
          </Form.Item>
          
          <Form.Item name="classId" label="班级">
            <Select
              placeholder="选择班级"
              style={{ width: 150 }}
              allowClear
              onChange={handleFilterClassChange}
            >
              {classes.map(cls => (
                <Option key={cls.id} value={cls.id}>
                  {cls.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="violationType" label="违纪类型">
            <Select
              placeholder="选择类型"
              style={{ width: 150 }}
              allowClear
            >
              {violationTypes.map(type => (
                <Option key={type.value} value={type.value}>
                  {type.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="severity" label="严重程度">
            <Select
              placeholder="选择程度"
              style={{ width: 120 }}
              allowClear
            >
              {severityOptions.map(option => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="dateRange" label="时间范围">
            <RangePicker style={{ width: 240 }} />
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit" icon={<FilterOutlined />}>
                筛选
              </Button>
              <Button onClick={resetFilters}>
                重置
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Card>

      {/* 提醒信息 */}
      <Alert
        message="违纪管理提醒"
        description="支持上传图片、音频、视频等多媒体附件作为违纪依据，请及时处理违纪记录。"
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {/* 操作区域和表格 */}
      <Card title="违纪记录管理">
        <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'space-between' }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => openModal()}
          >
            新增违纪记录
          </Button>
          
          <Space>
            <Button icon={<HistoryOutlined />} onClick={handleExportRecords}>
              导出记录
            </Button>
          </Space>
        </div>

        <Table
          columns={columns}
          dataSource={violations}
          rowKey="id"
          loading={loading}
          pagination={pagination}
          onChange={handleTableChange}
          scroll={{ x: 1400 }}
        />
      </Card>

      {/* 新增/编辑模态框 */}
      <Modal
        title={editingRecord ? '编辑违纪记录' : '新增违纪记录'}
        open={modalVisible}
        onCancel={closeModal}
        footer={null}
        width={800}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                label="班级"
                rules={[{ required: true, message: '请选择班级' }]}
              >
                <Select
                  placeholder="请先选择班级"
                  value={selectedClassId}
                  onChange={handleClassChange}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                >
                  {classes.map(cls => (
                    <Option key={cls.id} value={cls.id}>
                      {cls.name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="student_id"
                label="学生"
                rules={[{ required: true, message: '请选择学生' }]}
              >
                <Select
                  placeholder={selectedClassId ? "选择学生" : "请先选择班级"}
                  disabled={!selectedClassId}
                  showSearch
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
                  }
                  onChange={(value) => {
                    const student = filteredStudents.find(s => s.id === value);
                    if (student) {
                      form.setFieldsValue({
                        student_name: student.student_name,
                        class_id: student.class_id,
                      });
                    }
                  }}
                >
                  {filteredStudents.map(student => (
                    <Option key={student.id} value={student.id}>
                      {student.student_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          
          <Form.Item name="student_name" label="学生姓名" style={{ display: 'none' }}>
            <Input />
          </Form.Item>
          <Form.Item name="class_id" label="班级ID" style={{ display: 'none' }}>
            <Input />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item
                name="violation_type"
                label="违纪类型"
                rules={[{ required: true, message: '请选择违纪类型' }]}
              >
                <Select placeholder="选择违纪类型">
                  {violationTypes.map(type => (
                    <Option key={type.value} value={type.value}>
                      <Tag color={type.color}>{type.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="severity"
                label="严重程度"
                rules={[{ required: true, message: '请选择严重程度' }]}
              >
                <Select placeholder="选择严重程度">
                  {severityOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      <Tag color={option.color}>{option.label}</Tag>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="title"
            label="违纪标题"
            rules={[{ required: true, message: '请输入违纪标题' }]}
          >
            <Input placeholder="请输入违纪标题" />
          </Form.Item>

          <Form.Item
            name="description"
            label="违纪描述"
            rules={[{ required: true, message: '请输入违纪描述' }]}
          >
            <TextArea rows={4} placeholder="请详细描述违纪行为" />
          </Form.Item>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="location" label="发生地点">
                <Input placeholder="请输入发生地点" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="violation_date" label="违纪时间">
                <DatePicker
                  showTime
                  style={{ width: '100%' }}
                  placeholder="选择违纪时间"
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="points_deducted" label="扣除积分">
                <InputNumber min={0} step={0.1} precision={1} style={{ width: '100%' }} placeholder="输入扣除积分" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="status" label="处理状态">
                <Select placeholder="选择处理状态">
                  {statusOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      <Badge color={option.color} text={option.label} />
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="punishment" label="处理措施">
            <TextArea rows={3} placeholder="请输入处理措施" />
          </Form.Item>

          <Form.Item label="违纪依据附件">
            <Alert
              message="支持上传图片、音频、视频文件作为违纪依据"
              type="info"
              style={{ marginBottom: '16px' }}
            />
            <Upload
              listType="picture-card"
              fileList={fileList}
              onChange={handleUploadChange}
              onPreview={handlePreview}
              beforeUpload={() => false} // 阻止自动上传
              accept="image/*,audio/*,video/*"
              multiple
            >
              {fileList.length >= 8 ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>上传附件</div>
                </div>
              )}
            </Upload>
          </Form.Item>

          <Form.Item>
            <Space>
              <Button type="primary" htmlType="submit">
                {editingRecord ? '更新' : '创建'}
              </Button>
              <Button onClick={closeModal}>取消</Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      {/* 详情模态框 */}
      <Modal
        title="违纪记录详情"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            关闭
          </Button>
        ]}
        width={800}
      >
        {selectedRecord && (
          <div>
            <Descriptions bordered column={2}>
              <Descriptions.Item label="学生姓名">
                {selectedRecord.student_name}
              </Descriptions.Item>
              <Descriptions.Item label="班级">
                {selectedRecord.class_name}
              </Descriptions.Item>
              <Descriptions.Item label="违纪类型">
                {(() => {
                  const option = violationTypes.find(item => item.value === selectedRecord.violation_type);
                  return <Tag color={option?.color}>{option?.label}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="严重程度">
                {(() => {
                  const option = severityOptions.find(item => item.value === selectedRecord.severity);
                  return <Tag color={option?.color}>{option?.label}</Tag>;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="违纪标题" span={2}>
                {selectedRecord.title}
              </Descriptions.Item>
              <Descriptions.Item label="违纪描述" span={2}>
                {selectedRecord.description}
              </Descriptions.Item>
              <Descriptions.Item label="发生地点">
                {selectedRecord.location || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="违纪时间">
                {moment(selectedRecord.violation_date).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
              <Descriptions.Item label="扣除积分">
                {selectedRecord.points_deducted > 0 ? (
                  <Text type="danger">-{selectedRecord.points_deducted}</Text>
                ) : '-'}
              </Descriptions.Item>
              <Descriptions.Item label="处理状态">
                {(() => {
                  const option = statusOptions.find(item => item.value === selectedRecord.status);
                  return <Badge color={option?.color} text={option?.label} />;
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="处理措施" span={2}>
                {selectedRecord.punishment || '-'}
              </Descriptions.Item>
              <Descriptions.Item label="记录教师">
                {selectedRecord.teacher_name}
              </Descriptions.Item>
              <Descriptions.Item label="创建时间">
                {moment(selectedRecord.created_at).format('YYYY-MM-DD HH:mm')}
              </Descriptions.Item>
            </Descriptions>

            {selectedRecord.attachments && selectedRecord.attachments.length > 0 && (
              <div style={{ marginTop: '24px' }}>
                <Title level={5}>违纪依据附件</Title>
                <div style={{ marginTop: '16px' }}>
                  {renderAttachments(selectedRecord.attachments)}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* 图片预览 */}
      <Modal
        open={previewVisible}
        title="图片预览"
        footer={null}
        onCancel={() => setPreviewVisible(false)}
      >
        <img alt="preview" style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </div>
  );
};

export default StudentViolations;
